import { Directive, DoCheck, ElementRef, OnDestroy, OnInit, inject, input } from '@angular/core';
import { isValid, parse } from 'date-fns';

import { DatePicker } from 'primeng/datepicker';

/** Matches a complete `dd.MM.yyyy` and nothing else — tied to {@link DATE_PICKER_FORMAT}. */
const DATE_ONLY = /^\d{2}\.\d{2}\.\d{4}$/;
/** The only PrimeNG `dateFormat` this directive knows how to parse (see primeng-translation.ts). */
const DATE_PICKER_FORMAT = 'dd.mm.yy';
const DATE_FORMAT = 'dd.MM.yyyy';
const DATE_TIME_FORMAT = 'dd.MM.yyyy HH:mm';

export interface U2bTimeOfDay {
    hours: number;
    minutes: number;
}

/**
 * Makes a `<p-datepicker [showTime]>` usable with a typed date and an explicit
 * confirmation, together with `U2bDatePickerButtonBarComponent`.
 *
 * Four PrimeNG behaviours are adjusted (verified against primeng@21.1.8):
 *
 * 1. `parseDateTime()` splits typed text on spaces and treats the last token as the
 *    time, so a date typed without one throws and `onUserInput()` sets the model to
 *    `null` — the field then clears on blur. The instance's `parseValueFromString()`
 *    is patched to complete such input with a time first.
 * 2. `hideOnDateTimeSelect` closes the panel 150 ms after a day is clicked, before the
 *    time can be adjusted. It is kept off while a time is shown; "Übernehmen" closes.
 * 3. `onUserInput()` ignores input that did not come from a keystroke (paste, autofill),
 *    and the text mask conforms the value only after PrimeNG has parsed it. A blur
 *    listener re-parses the raw text before PrimeNG rewrites the field from the model.
 * 4. `onInputKeydown()` treats Enter as "close the panel" only, so the field keeps the
 *    raw text the user typed ("05.09.2026" without its time). Enter now commits like
 *    "Übernehmen" does, which rewrites the field from the model.
 *
 * It also exposes the panel clock so `U2bDatePickerTimeInputComponent` can offer a
 * typable hour/minute — PrimeNG renders both as read-only `<span>`s.
 */
@Directive({
    selector: '[u2bDatePickerApply]',
    standalone: true,
    exportAs: 'u2bDatePickerApply',
})
export class U2bDatePickerApplyDirective implements OnInit, DoCheck, OnDestroy {

    /** Time of day for a typed date when the field has no value to take one from. */
    readonly u2bDatePickerFallbackTime = input<U2bTimeOfDay | null>(null);

    private readonly picker = inject(DatePicker);
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

    private originalParse: ((text: string) => Date | Date[] | null) | null = null;

    ngOnInit(): void {
        const original = this.picker.parseValueFromString;

        if (typeof original === 'function') {
            this.originalParse = original;
            this.picker.parseValueFromString = (text: string) =>
                original.call(this.picker, this.completeTypedText(text));
        }

        // Capture phase on the host, which is an ancestor of the inner <input>: runs before
        // PrimeNG's own (blur) binding on that input. `blur` does not bubble but does capture.
        this.host.nativeElement.addEventListener('blur', this.onBlurCapture, true);
        this.host.nativeElement.addEventListener('keydown', this.onKeydownCapture, true);
    }

    ngDoCheck(): void {
        // `showTime` is bound dynamically in places (hourly services), and PrimeNG reads this
        // flag at click time — closing on day-click stays right when there is no time to adjust.
        this.picker.hideOnDateTimeSelect = !this.supportsTypedTime();
    }

    ngOnDestroy(): void {
        if (this.originalParse) {
            this.picker.parseValueFromString = this.originalParse;
        }
        this.host.nativeElement.removeEventListener('blur', this.onBlurCapture, true);
        this.host.nativeElement.removeEventListener('keydown', this.onKeydownCapture, true);
    }

    /** Commits whatever the field currently holds and closes the panel. */
    apply(): void {
        const text = this.inputElement()?.value.trim() ?? '';

        // An unparseable or out-of-range value is not silently discarded: nothing is committed and
        // updateInputfield() below restores the last valid value, as PrimeNG's blur handling does.
        const value = text === '' ? null : this.parse(text);

        if (text === '' || value) {
            this.commit(value);
        }

        this.close();
    }

    /**
     * Closes the panel on the value as it now stands, without re-reading the field. The clock
     * writes its changes through Angular, so the field still shows the previous text within the
     * same tick — {@link apply} would read that back and, on a first-ever time, mistake the
     * still-empty field for "cleared" and drop the value again.
     */
    close(): void {
        this.picker.updateInputfield();
        this.picker.hideOverlay();
    }

    /** Keeps PrimeNG's panel keyboard handling (Escape closes, Tab is trapped) on custom buttons. */
    onButtonKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            // Not `onContainerButtonKeydown`: that one assigns `overlayVisible` bare, which only
            // repaints because the stock buttons live inside the picker's own (OnPush) view.
            // Called from here it would leave the panel on screen; hideOverlay() marks it dirty.
            this.picker.hideOverlay();
            event.preventDefault();

            return;
        }

        this.picker.onContainerButtonKeydown(event);
    }

    today(event: Event): void {
        this.picker.onTodayButtonClick(event);
    }

    clear(event: Event): void {
        this.picker.onClearButtonClick(event);
    }

    /** Whether the panel carries a clock the custom time row can drive (no seconds field). */
    showsTime(): boolean {
        return !!this.picker.showTime && !this.picker.showSeconds;
    }

    /** The panel clock, normalised to 24 hours whatever `hourFormat` the picker runs in. */
    panelTime(): U2bTimeOfDay {
        const hours = this.picker.currentHour ?? 0;

        return {
            hours: this.picker.hourFormat === '12'
                ? this.picker.convertTo24Hour(hours, !!this.picker.pm)
                : hours,
            minutes: this.picker.currentMinute ?? 0,
        };
    }

    /**
     * Sets the panel clock and writes it through to the model, the same way PrimeNG's own
     * increment buttons do: out-of-range values are pulled back by `constrainTime()`
     * (`minDate`/`maxDate` on the selected day), so a typed time cannot escape the range.
     */
    setPanelTime({ hours, minutes }: U2bTimeOfDay): void {
        const h = clamp(hours, 0, 23);
        const m = clamp(minutes, 0, 59);
        const pm = h > 11;
        const displayHour = this.picker.hourFormat === '12' ? (h % 12 || 12) : h;

        this.picker.toggleAMPMIfNotMinDate(pm);
        [this.picker.currentHour, this.picker.currentMinute, this.picker.currentSecond] =
            this.picker.constrainTime(displayHour, m, this.picker.currentSecond ?? 0, pm);
        this.picker.updateTime();
    }

    /** One `stepHour`/`stepMinute` up (+1) or down (-1); PrimeNG wraps around midnight. */
    stepTime(unit: 'hours' | 'minutes', direction: 1 | -1, event: Event): void {
        if (unit === 'hours') {
            direction > 0 ? this.picker.incrementHour(event) : this.picker.decrementHour(event);
        } else {
            direction > 0 ? this.picker.incrementMinute(event) : this.picker.decrementMinute(event);
        }

        // The stock buttons defer this to their mouseup handler; ours commit right away.
        this.picker.updateTime();
    }

    private readonly onKeydownCapture = (event: KeyboardEvent): void => {
        if (event.key !== 'Enter' || event.target !== this.inputElement()) {
            return;
        }

        // PrimeNG's Enter only hides the panel, leaving the raw text in the field — a date
        // typed without a time then stays visibly incomplete. Commit it as "Übernehmen" does
        // and keep PrimeNG's handler (and any surrounding form submit) out of it.
        event.preventDefault();
        event.stopPropagation();
        this.apply();
    };

    private readonly onBlurCapture = (): void => {
        const text = this.inputElement()?.value.trim();
        const value = text ? this.parse(text) : null;

        // PrimeNG's blur handler rewrites the field from the model; commit the typed text first
        // so an entry that never hit the completion above survives instead of being wiped. An
        // empty or unparseable field is left to PrimeNG — clearing is its job, not ours.
        if (value) {
            this.commit(value);
        }
    };

    /**
     * Writes a parsed value through, but only when it is both selectable (PrimeNG's own
     * `minDate`/`maxDate`/`disabledDates` check, which the typing path applies too) and actually
     * different — every `updateModel` emits, and a no-op blur must not look like an edit.
     */
    private commit(value: Date | null): void {
        if (value && !this.picker.isValidSelection(value)) {
            return;
        }

        const current = this.picker.value as Date | null;
        const unchanged = value && current instanceof Date
            ? current.getTime() === value.getTime()
            : current === value;

        if (!unchanged) {
            this.picker.updateModel(value);
        }
    }

    /** Appends a time segment to a bare date so PrimeNG's own parser can split it. */
    private completeTypedText(text: string): string {
        const trimmed = text?.trim() ?? '';

        if (!this.supportsTypedTime() || !DATE_ONLY.test(trimmed)) {
            return text;
        }

        const { hours, minutes } = this.timeOfDay();

        return `${trimmed} ${pad(hours)}:${pad(minutes)}`;
    }

    private parse(text: string): Date | null {
        if (this.picker.dateFormat !== DATE_PICKER_FORMAT) {
            return null;
        }

        if (this.picker.showTime) {
            const withTime = parse(text, DATE_TIME_FORMAT, new Date());
            if (isValid(withTime)) {
                return withTime;
            }
        }

        const dateOnly = parse(text, DATE_FORMAT, new Date());
        if (!isValid(dateOnly)) {
            return null;
        }

        if (this.picker.showTime) {
            const { hours, minutes } = this.timeOfDay();
            dateOnly.setHours(hours, minutes, 0, 0);
        }

        return dateOnly;
    }

    /**
     * Keeping the current value's time of day matters where a changed time marks the field as
     * manually edited (see date-range-step's `reapplyDefaultTimes`). Without a value the caller's
     * default wins; midnight is the last resort, never the panel's clock — that one is seeded
     * from "now" for an untouched field, which has nothing to do with the date being typed.
     */
    private timeOfDay(): U2bTimeOfDay {
        const value = this.picker.value as Date | null;

        if (value instanceof Date && isValid(value)) {
            return { hours: value.getHours(), minutes: value.getMinutes() };
        }

        return this.u2bDatePickerFallbackTime() ?? { hours: 0, minutes: 0 };
    }

    /** The shortcut only holds for a single 24-hour value in the app-wide format, without seconds. */
    private supportsTypedTime(): boolean {
        return !!this.picker.showTime
            && this.picker.dateFormat === DATE_PICKER_FORMAT
            && this.picker.hourFormat === '24'
            && !this.picker.showSeconds
            && this.picker.selectionMode === 'single';
    }

    private inputElement(): HTMLInputElement | null {
        return this.picker.inputfieldViewChild?.nativeElement ?? null;
    }
}

function pad(value: number): string {
    return `${value}`.padStart(2, '0');
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}
